import React from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Box,
  Button,
  Chip,
} from "@mui/material";
import {
  Edit as EditIcon,
  Visibility as VisibilityIcon,
  BarChart as BarChartIcon,
} from "@mui/icons-material";
import AddIcon from "@mui/icons-material/Add";
import { Organization } from "../../api/organization";

interface OrganizationListProps {
  organizations: Organization[];
  organizationUserCounts: Record<string, number>;
  organizationBrandCounts: Record<string, number>;
  onCreateOrganization: () => void;
  onEditOrganization: (organization: Organization) => void;
  onViewDetails: (organization: Organization) => void;
  onShowGlobalUsage: () => void;
}

export const OrganizationList: React.FC<OrganizationListProps> = ({
  organizations,
  organizationUserCounts,
  organizationBrandCounts,
  onCreateOrganization,
  onEditOrganization,
  onViewDetails,
  onShowGlobalUsage,
}) => {
  return (
    <>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <Box sx={{ fontSize: "1.5rem", fontWeight: 500 }}>Organizations</Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<BarChartIcon />}
            onClick={onShowGlobalUsage}
          >
            Global AI Usage
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={onCreateOrganization}
          >
            Create Organization
          </Button>
        </Box>
      </Box>

      <TableContainer component={Paper}>
        <Table aria-label="organizations table">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell align="center">Clients</TableCell>
              <TableCell align="center">Brands</TableCell>
              <TableCell align="center">Permissions</TableCell>
              <TableCell align="center">Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {organizations.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  No organizations found
                </TableCell>
              </TableRow>
            ) : (
              organizations.map((org) => {
                const userCount = organizationUserCounts[org.id] || 0;
                const brandCount = organizationBrandCounts[org.id] || 0;

                return (
                  <TableRow
                    key={org.id}
                    hover
                    sx={{
                      cursor: "pointer",
                      opacity: org.disabled ? 0.6 : 1,
                      backgroundColor: org.disabled ? "#fafafa" : "inherit",
                    }}
                    onClick={() => onViewDetails(org)}
                  >
                    <TableCell>{org.name}</TableCell>
                    <TableCell align="center">
                      <Chip label={userCount} size="small" color="primary" />
                    </TableCell>
                    <TableCell align="center">
                      <Chip label={brandCount} size="small" color="secondary" />
                    </TableCell>
                    <TableCell align="center">
                      <Box
                        sx={{
                          display: "flex",
                          gap: 0.5,
                          justifyContent: "center",
                        }}
                      >
                        <Chip label="Review" size="small" variant="outlined" />

                        {org.mode_allow_create &&
                        org.mode_allow_ai_generation ? (
                          <Chip
                            label="Create + AI"
                            size="small"
                            variant="outlined"
                          />
                        ) : (
                          <>
                            {org.mode_allow_create && (
                              <Chip
                                label="Create"
                                size="small"
                                variant="outlined"
                              />
                            )}
                            {org.mode_allow_ai_generation && (
                              <Chip
                                label="AI Gen"
                                size="small"
                                variant="outlined"
                              />
                            )}
                          </>
                        )}

                        {org.mode_allow_translate &&
                        org.mode_allow_translate_ai ? (
                          <Chip
                            label="Translate + AI"
                            size="small"
                            variant="outlined"
                          />
                        ) : (
                          <>
                            {org.mode_allow_translate && (
                              <Chip
                                label="Translate"
                                size="small"
                                variant="outlined"
                              />
                            )}
                            {org.mode_allow_translate_ai && (
                              <Chip
                                label="AI Translate"
                                size="small"
                                variant="outlined"
                              />
                            )}
                          </>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      {org.disabled ? (
                        <Chip label="Disabled" size="small" color="error" />
                      ) : (
                        <Chip label="Active" size="small" color="success" />
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        onClick={(e) => {
                          e.stopPropagation();
                          onViewDetails(org);
                        }}
                        size="small"
                        title="View Details"
                      >
                        <VisibilityIcon />
                      </IconButton>
                      <IconButton
                        onClick={(e) => {
                          e.stopPropagation();
                          onEditOrganization(org);
                        }}
                        size="small"
                        title="Edit"
                      >
                        <EditIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
};
